import React, { useState } from 'react'

import * as FEATURES from 'constants/feature-keys'

import {
  AnalyticsClientProvider,
  BuildSelector,
  EnvRecomposeProvider,
  Environment,
  EnvironmentHooksProvider,
  ProgressProvider,
  PromptPortalProvider,
  PromptProviderV3 as PromptProvider,
  RemoteStringsProvider,
  useUserScopedLocalStorage,
  useUserScopedSessionStorage,
} from '@acorns/web-utils'
import { UserIdProvider } from '@acorns/web-utils'
import { ThemeProvider } from 'styled-components'

import { DatadogUserId } from 'components/DatadogUserId'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { TrackAppLoadTime } from 'components/TrackAppLoadTime'
import { WebAppTogglesProvider } from 'providers/WebAppTogglesProvider'
import { initializeBrazeClient } from 'utils/braze'
import { MFE_CONFIG } from 'utils/micro-frontends/config'
import { PromptAdapter } from 'utils/prompts'
import {
  getTogglesEnvIndex,
  setTogglesEnvIndexOverride,
} from 'utils/toggles-utils'

import { Provider as ApolloProvider } from './config/apollo'
import { LearnTipsStateProvider } from './providers/LearnTipsStateProvider'
import Router from './router'
import theme from './theme'
import {
  Provider as ApolloErrorHandlingProvider,
  DEFAULT_ERROR_DETAILS,
} from './utils/error'
import withLogVersion from './utils/with-log-version'

type Props = {
  environment: Environment
}

function UserScopedStorage() {
  useUserScopedLocalStorage({
    addStorageToGlobalScope: true,
  })

  useUserScopedSessionStorage({
    addStorageToGlobalScope: true,
  })

  return null
}

export default withLogVersion((props: Props) => {
  const buildEnv = props.environment.get('buildEnv')

  initializeBrazeClient(
    props.environment.get('braze'),
    props.environment.get('brazeSdkUrl'),
    buildEnv !== 'production',
  )

  const [togglesEnv, setTogglesEnv] = useState<number>(getTogglesEnvIndex())

  const handleTogglesEnvToggle = (index: number) => {
    setTogglesEnv(index)
    setTogglesEnvIndexOverride(index)
  }

  return (
    <UserIdProvider>
      <UserScopedStorage />
      <ThemeProvider theme={theme}>
        <AnalyticsClientProvider
          rudderStack={{
            dataPlaneUrl: props.environment.get('rudderStackDataPlaneUrl'),
            writeKey: props.environment.get('rudderStackWriteKey'),
          }}
        >
          <TrackAppLoadTime />
          <DatadogUserId />
          <WebAppTogglesProvider>
            <RemoteStringsProvider>
              <ErrorBoundary>
                <EnvironmentHooksProvider env={props.environment}>
                  <EnvRecomposeProvider env={props.environment}>
                    <ApolloErrorHandlingProvider
                      defaultErrorDetails={DEFAULT_ERROR_DETAILS}
                    >
                      <ApolloProvider
                        graphqlUrl={props.environment.get('graphql')}
                      >
                        <PromptAdapter>
                          <PromptPortalProvider>
                            <PromptProvider>
                              <LearnTipsStateProvider>
                                <ProgressProvider>
                                  <Router />
                                  {buildEnv !== 'production' && (
                                    <BuildSelector
                                      projectNames={Object.keys(MFE_CONFIG)}
                                      featureNames={Object.values(FEATURES)}
                                      handleEnvToggle={handleTogglesEnvToggle}
                                      togglesEnv={togglesEnv}
                                    />
                                  )}
                                </ProgressProvider>
                              </LearnTipsStateProvider>
                            </PromptProvider>
                          </PromptPortalProvider>
                        </PromptAdapter>
                      </ApolloProvider>
                    </ApolloErrorHandlingProvider>
                  </EnvRecomposeProvider>
                </EnvironmentHooksProvider>
              </ErrorBoundary>
            </RemoteStringsProvider>
          </WebAppTogglesProvider>
        </AnalyticsClientProvider>
      </ThemeProvider>
    </UserIdProvider>
  )
})
